import React from 'react';
import { Box, Container, Typography, Grid } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Section = styled(Box)(({ theme }) => ({
  padding: '120px 0',
  background: theme.palette.background.default,
  borderTop: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.down('md')]: {
    padding: '72px 0',
  },
}));

const Eyebrow = styled(Typography)(({ theme }) => ({
  fontFamily: '"JetBrains Mono", ui-monospace, monospace',
  fontSize: '0.8125rem',
  fontWeight: 500,
  color: theme.palette.primary.main,
  marginBottom: '12px',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
}));

const SectionHeading = styled(Typography)(({ theme }) => ({
  fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
  fontWeight: 700,
  letterSpacing: '-0.02em',
  color: theme.palette.text.primary,
  marginBottom: '56px',
  maxWidth: '720px',
  [theme.breakpoints.down('md')]: {
    marginBottom: '36px',
  },
}));

const Card = styled(Box)(({ theme }) => ({
  padding: '28px',
  borderRadius: '14px',
  border: `1px solid ${theme.palette.divider}`,
  background: theme.palette.background.paper,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  animation: `${fadeIn} 0.4s ease-out`,
  transition: 'border-color 0.2s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
  },
  [theme.breakpoints.down('md')]: {
    padding: '20px',
  },
}));

const Period = styled(Typography)(({ theme }) => ({
  fontFamily: '"JetBrains Mono", ui-monospace, monospace',
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  marginBottom: '14px',
}));

const Degree = styled(Typography)(({ theme }) => ({
  fontSize: '1.125rem',
  fontWeight: 600,
  letterSpacing: '-0.01em',
  color: theme.palette.text.primary,
  marginBottom: '6px',
  lineHeight: 1.3,
}));

const Institution = styled(Typography)(({ theme }) => ({
  fontSize: '0.9375rem',
  color: theme.palette.text.secondary,
  marginBottom: '20px',
  '& .sep': { margin: '0 8px', opacity: 0.5 },
}));

const FocusList = styled(Box)(({ theme }) => ({
  marginTop: 'auto',
  paddingTop: '16px',
  borderTop: `1px solid ${theme.palette.divider}`,
}));

const FocusItem = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
  lineHeight: 1.7,
  display: 'flex',
  gap: '8px',
  marginBottom: '6px',
  '&::before': {
    content: '"·"',
    color: theme.palette.primary.main,
    fontWeight: 700,
  },
}));

const Education = () => {
  const items = [
    {
      degree: 'M.S. Computer Science',
      institution: 'North Dakota State University',
      location: 'Fargo, ND',
      period: 'Aug 2023 - Dec 2025',
      focus: [
        'Research: LLM-assisted software testing',
        'Algorithms, software engineering',
        'Distributed systems coursework',
      ],
    },
    {
      degree: 'B.S. Information Science',
      institution: 'Technical University of Kenya',
      location: 'Nairobi, KE',
      period: 'Sep 2016 - Nov 2020',
      focus: [
        'Information systems and databases',
        'Web development fundamentals',
        'IT infrastructure and networking',
      ],
    },
    {
      degree: 'Software Development Certificate',
      institution: 'Moringa School / Flatiron School',
      location: 'Nairobi, KE',
      period: 'Nov 2022 - Jun 2023',
      focus: [
        'Full-stack web development',
        'Modern JavaScript and React',
        'Agile and team workflows',
      ],
    },
  ];

  return (
    <Section id="education">
      <Container maxWidth="lg">
        <Eyebrow>Education</Eyebrow>
        <SectionHeading>Where I studied.</SectionHeading>

        <Grid container spacing={3}>
          {items.map((e) => (
            <Grid item xs={12} md={4} key={e.degree}>
              <Card>
                <Period>{e.period}</Period>
                <Degree>{e.degree}</Degree>
                <Institution>
                  {e.institution}
                  <span className="sep">·</span>
                  {e.location}
                </Institution>
                <FocusList>
                  {e.focus.map((f) => (
                    <FocusItem key={f}>{f}</FocusItem>
                  ))}
                </FocusList>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Section>
  );
};

export default Education;
